import { useSortable } from '@dnd-kit/sortable';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft } from 'shared/assets/icons';
import { Collapse } from 'shared/components/ui/collapse';
import { Colors, IconButton, Sizes } from 'shared/components/ui/icon-button';
import { ID } from 'shared/types/id';

import * as Styled from './side-navigation-panel.style';

export type SideNavigationPanelSectionProps = {
  id: ID;
  title: string;
  open: boolean;
  onToggle: () => void;
  enabledCount: number;
  totalCount: number;
  isFirst?: boolean;
  children: React.ReactNode;
};

// Deliberately not NavigationSectionLayout: here the header IS the drag handle and its open state is persisted per section.
export const SideNavigationPanelSection: React.FC<
  SideNavigationPanelSectionProps
> = ({
  id,
  title,
  open,
  onToggle,
  enabledCount,
  totalCount,
  isFirst,
  children,
}) => {
  const { t } = useTranslation();
  const {
    setNodeRef,
    attributes,
    listeners,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  return (
    <Styled.Section
      ref={setNodeRef}
      $transform={transform}
      $transition={transition}
      $isFirst={!!isFirst}
      $isDragging={isDragging}
    >
      <Styled.SectionHeader
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        {...attributes}
        {...listeners}
      >
        <Styled.SectionTitle>
          {title}
          <Styled.SectionCount>
            {t('general.labels.count-of-total', {
              count: enabledCount,
              total: totalCount,
            })}
          </Styled.SectionCount>
        </Styled.SectionTitle>

        {/* A span, not a button: the header itself is the button and owns aria-expanded. */}
        <IconButton
          as="span"
          icon={ChevronLeft}
          rotation={open ? -90 : 0}
          size={Sizes.Small}
          color={Colors.Default}
        />
      </Styled.SectionHeader>

      {/* Unmounted, not collapsed: an animating height is measured mid-drag and offsets every drop position. */}
      {!isDragging && (
        <Collapse isOpen={open}>
          <Styled.List>{children}</Styled.List>
        </Collapse>
      )}
    </Styled.Section>
  );
};
